<template>
    <div class="card">

        <div class="bg-blue-light p-1">
            <TitleButton 
                :showBtn="false"
            />
        </div>

        <div class="px-2">
            <DateQuerySetter
                btnTitle="Go"
                @onClickGo="onSearch"
                :isHideDate="true"
            >
                <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                    <label 
                        for="colFormLabel" 
                        class="col-form-label col-xl-3"
                    >
                        From
                    </label>
                    <input
                        v-model="params.start_date"
                        type="date"
                        class="form-control date-mask" 
                        placeholder="From"
                    >
                </div>
                
                <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                    <label 
                        for="colFormLabel"
                        class="col-form-label 'col-xl-3'"
                    >
                        To
                    </label>
                    <input
                        v-model="params.end_date"
                        type="date"
                        class="form-control date-mask" 
                        placeholder="From"
                    >   
                </div>

                <div class="col-12 col-sm-6 col-md-3 col-lg-3">
                    <label for="colFormLabel" class="col-form-label">Patient</label>
                    <input type="text" class="form-control" name="name" v-model="contactInfo.full_name" disabled>
                </div>

                <div class="col-6 col-sm-3 col-md-3 col-lg-3">
                    <label for="colFormLabel" class="col-form-label">Admission no</label>
                    <AsyncSelect
                        placeholder="Admission no"
                        v-model="params.admissionNo"
                        :api-service="fetchIpdRegNumbers"
                        :additional-query="{}"
                        :format-label="formatPatientLabel"
                    />
                </div>

                <div class="col-12 col-sm-6 col-md-4 col-lg-4">
                    <label for="colFormLabel" class="col-form-label">Registration status </label>
                    <v-select 
                        v-model="params.regStatus"
                        placeholder="Select registration status" class="w-100" 
                        :options="registrationStatus" label="name"
                        :reduce="(name) => name.value" 
                    />
                </div>

                <div class="col-12 col-sm-6 col-md-4 col-lg-4">
                    <label for="colFormLabel" class="col-form-label">Select Consultant </label>
                    <AsyncSelect
                        placeholder="Select consutant"
                        v-model="params.doctor"
                        :api-service="fetchServiceList"
                        :additional-query="{ resource_type: 'human_resources' }"
                        :format-label="formatPatientLabel"
                    />
                </div>

            </DateQuerySetter>
        </div>

        <div class="col-12 px-2 mt-2 mb-5">
            <ListTable :tableItems="tableItems"/>
        </div>

        <div class="px-2 position-absolute bottom-0">
            <Pagination :offset="params.offset" @onPageChange="onPageChange" ref="pagination"/>
        </div>

        <Loader v-if="loading"/>

    </div>

</template>

<script setup>
    import TitleButton from '@/components/atom/TitleButton';
    import ListTable from '@/components/molecule/company/hospital/ipd-station/IpdStationTable.vue';
    import { computed, inject, onMounted, reactive, ref, watch } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import handleHospital from '@/services/modules/hospital'
    import Loader from '@/components/atom/LoaderComponent'
    import Pagination from '@/components/atom/Pagination'
    import { useStore } from 'vuex';
    import DateQuerySetter from '@/components/atom/DateQuerySetter.vue';
    import handleContact from "@/services/modules/contact";
    import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
    import { useAsyncDropdownHelper } from "@/services/utils/asyncDropdownHelper";

    const $router = useRouter();
    const $route = useRoute();
    const $store = useStore();
    const showError = inject('showError');
    const showSuccess = inject('showSuccess');
    const loading = ref(false)
    const { fetchContactProfiles } = handleContact()
    const { formatPatientLabel } = useAsyncDropdownHelper()
    const { 
        fetchIPDRegistrationList, 
        fetchIpdRegStatus, 
        fetchServiceList, 
        fetchIpdRegNumbers 
    } = handleHospital()
    const params = reactive({
        start_date: $route.query.start,
        end_date: $route.query.end,
        status: null,
        offset: 20,
        patient: null,
        doctor: null,
        regStatus: null,
        admissionNo: null,
    });

    const props = defineProps({
        contactInfo: {
            type: Object,
            default: {}
        }
    })

    const registrationStatus = reactive([])
    const tableItems = ref([]);

    const start = computed(() => $route.query.start);
    const end = computed(() => $route.query.end);
    const patientId = computed(() => props.contactInfo.id);

    //watch
    watch(patientId, () => {
        params.start_date = $route.query.start,
        params.end_date = $route.query.end,
        getRegistrationList();
    })

    //Functions
    const setPaginationData = (data) => {
        $store.commit('setPaginationData', data)
    };

    const onSearch = () => {
        getRegistrationList();
    }

    const setPagination = (data) => {
        setPaginationData({
            records: data.total,
            perPage: data.per_page,
            currentPage: data.current_page,
        })
    }

    async function onPageChange(page) {
        let routeQuery = Object.assign({}, $route.query)
        routeQuery.page = page
        $router.push({path: $route.path, query: routeQuery})
        await getRegistrationList();
    }

    function getQuery() {
        let query = `?company_id=${$route.params.companyId}`;
        query += '&patient_id=' + patientId.value
        if ($route.query.page) query += '&page=' + $route.query.page
        if (params.start_date) query += '&start_date=' + params.start_date
        if (params.end_date) query += '&end_date=' + params.end_date
        if (params.regStatus) query += '&reg_status=' + params.regStatus
        if (params.doctor) query += '&doctor_id=' + params.doctor.id
        if (params.admissionNo) query += '&admission_id=' + params.admissionNo.id
        query += '&offset=' + params.offset
        return query;
    }

    const getRegistrationList = async () => {
        let query = getQuery();
        loading.value = true
        await fetchIPDRegistrationList(query).then(res => {
            loading.value = false
            if (!res.status){
                $store.commit("resetPagination")
                return showError(res.message)
            }
            tableItems.value = res.data.data;
            setPagination(res.data.meta)
        }).catch(err => {
            console.log(err)
        }).finally(() => {
            loading.value = false
        })
    }

    const getRegStatus = () => {
        let query = `?company_id=${$route.params.companyId}`;
        fetchIpdRegStatus(query).then(res=> {
            if(!res.data) return;
            registrationStatus.push(...res.data);
            params.regStatus = 'admitted';
        })
    }

    onMounted( async() => {
        await Promise.all([
            getRegStatus(),
            getRegistrationList()
        ])
    });
</script>

<style>

</style>